import React, { useState, useEffect } from "react";
import BaseEmailComponent from "../BaseEmailComponent";
import EmailImageUpload from "../EmailImageUpload";
import { Form, Row, Col } from "react-bootstrap";

const MultiColumnLayout = ({ id, data, onUpdate, styleSettings, columns = 3 }) => {
  // State for column data
  const [columnItems, setColumnItems] = useState(data?.columnItems || Array(columns).fill().map(() => ({
    title: "",
    description: "",
    image: null
  })));

  // Update state when data changes
  useEffect(() => {
    if (data && data.columnItems) {
      const items = [...data.columnItems];
      // Ensure we have the right number of columns
      while (items.length < columns) {
        items.push({ title: "", description: "", image: null });
      }
      setColumnItems(items.slice(0, columns));
    }
  }, [data, columns]);

  // Handle updating a specific column
  const handleColumnUpdate = (index, field, value) => {
    const updatedColumns = [...columnItems];
    updatedColumns[index] = {
      ...updatedColumns[index],
      [field]: value
    };
    setColumnItems(updatedColumns);
  };

  // Edit UI with forms for each column
  const renderEditUI = ({ title, setTitle, description, setDescription, handleSaveChanges, styleSettings }) => {
    // Function to save all changes
    const saveAllChanges = () => {
      handleSaveChanges(title, description, { columnItems });
    };

    return (
      <div className="p-3 rounded" style={{ backgroundColor: 'white' }}>
        <Form.Group className="mb-3">
          <Form.Label><strong>Section Title</strong></Form.Label>
          <Form.Control
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={saveAllChanges}
            placeholder="Enter Section Title"
          />
        </Form.Group>
        
        <Form.Group className="mb-4">
          <Form.Label><strong>Section Description</strong></Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={saveAllChanges}
            placeholder="Enter Section Description"
          />
        </Form.Group>
        
        <div className="mb-3">
          <strong>Column Content</strong>
        </div>
        
        <Row>
          {columnItems.map((column, index) => (
            <Col md={12 / columns} key={index} className="mb-4">
              <div className="border rounded p-3">
                <strong className="d-block mb-2">Column {index + 1}</strong>
                
                <EmailImageUpload
                  initialImage={column.image}
                  onImageUpload={(imageData) => {
                    handleColumnUpdate(index, "image", imageData);
                    saveAllChanges();
                  }}
                  allowRegenerate={true}
                  label={`Column ${index + 1} Image`}
                />
                
                <Form.Group className="mb-2 mt-3">
                  <Form.Label><small>Title</small></Form.Label>
                  <Form.Control
                    type="text"
                    value={column.title}
                    onChange={(e) => handleColumnUpdate(index, "title", e.target.value)}
                    onBlur={saveAllChanges}
                    size="sm"
                    placeholder={`Column ${index + 1} Title`}
                  />
                </Form.Group>
                
                <Form.Group className="mb-2">
                  <Form.Label><small>Description</small></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={column.description}
                    onChange={(e) => handleColumnUpdate(index, "description", e.target.value)}
                    onBlur={saveAllChanges}
                    size="sm"
                    placeholder={`Column ${index + 1} Description`}
                  />
                </Form.Group>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  // View UI for display mode
  const renderViewUI = ({ title, description, styleSettings }) => {
    const { primaryColor, secondaryColor, fontFamily } = styleSettings || {};
    
    return (
      <div>
        {(title || description) && (
          <div className="text-center mb-4">
            {title && (
              <h2 style={{ 
                fontSize: "28px", 
                marginBottom: "12px",
                color: secondaryColor || "#a1894c",
                fontFamily,
                fontWeight: "bold"
              }}>
                {title}
              </h2>
            )}
            {description && (
              <p style={{ 
                fontFamily,
                color: primaryColor || "#333333",
                maxWidth: "800px",
                margin: "0 auto",
                lineHeight: "1.6"
              }}>
                {description}
              </p>
            )}
          </div>
        )}
        
        <Row>
          {columnItems.map((column, index) => (
            <Col md={12 / columns} key={index} className="mb-4 text-center">
              <div className="mb-3">
                {column.image ? (
                  <img 
                    src={column.image} 
                    alt={column.title || `Column ${index + 1}`} 
                    className="img-fluid rounded mx-auto" 
                    style={{ maxHeight: "180px" }}
                  />
                ) : (
                  <div 
                    className="d-flex align-items-center justify-content-center bg-light rounded mx-auto" 
                    style={{ height: "150px", width: "100%" }}
                  >
                    <img
                      src="/img-preview.svg"
                      alt="Column placeholder"
                      style={{ maxHeight: "80px" }}
                    />
                  </div>
                )}
              </div>
              
              {column.title && (
                <h4 style={{ 
                  fontSize: "18px", 
                  marginBottom: "10px",
                  color: secondaryColor || "#a1894c",
                  fontFamily
                }}>
                  {column.title}
                </h4>
              )}
              
              {column.description && (
                <p style={{ 
                  fontSize: "14px",
                  color: primaryColor || "#333333",
                  fontFamily,
                  lineHeight: "1.5"
                }}>
                  {column.description}
                </p>
              )}
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  return (
    <BaseEmailComponent
      id={id}
      data={data}
      onUpdate={onUpdate}
      componentName={`${columns}-Column Layout`}
      styleSettings={styleSettings}
      editUI={renderEditUI}
      viewUI={renderViewUI}
    />
  );
};

export default MultiColumnLayout; 